import { useEffect } from 'react';
import Field from '../../../components/common/Field';
import Spinner from '../../../components/common/Spinner';
import useSignupStore from '../../../store/useSignupStore';
import { showToast } from '../../../store/useToastStore';
import { IconCheck } from '../../../components/icons';
import { gradeMark } from './SchoolsStep';

/* shortest class name accepted (Mohit, 30 Sep) — the API itself has no minimum */
const MIN_CLASS_NAME = 2;

const roomParentCount = (n) =>
  n ? `(${n} room parent${n > 1 ? 's' : ''} already)` : '(no room parents yet)';

export default function ClassGroupStep({ onFinish }) {
  const { roomParentGrade, joinClassId, className, submitting, gradeClasses, classesLoading } = useSignupStore();
  const set = useSignupStore((s) => s.set);
  const joinedGrades = useSignupStore((s) => s.joinedGrades);
  const loadClassesForGrade = useSignupStore((s) => s.loadClassesForGrade);
  const submit = useSignupStore((s) => s.submit);

  /* Which classes already exist only matters once a grade is picked. */
  useEffect(() => {
    if (!roomParentGrade) return;
    loadClassesForGrade(roomParentGrade).catch((err) => showToast(err.message));
  }, [roomParentGrade, loadClassesForGrade]);

  const grades = joinedGrades();
  const existing = gradeClasses[roomParentGrade] || [];
  const joining = !!joinClassId;

  /* the grade has to be one they actually joined — re-picking schools can leave
     an answer here pointing at a group that's no longer in the list */
  const gradePicked = grades.some((g) => g.networkId === roomParentGrade);
  const nameLength = className.trim().length;
  /* typed but too short — say so, rather than leave Finish Setup greyed out */
  const nameTooShort = !joining && nameLength > 0 && nameLength < MIN_CLASS_NAME;
  const complete = gradePicked && (joining || nameLength >= MIN_CLASS_NAME);

  const pickGrade = (networkId) =>
    /* a different grade has its own classes, so any join picked under the old
       one no longer applies */
    set({ roomParentGrade: roomParentGrade === networkId ? '' : networkId, joinClassId: '' });

  const finish = async () => {
    if (!complete || submitting) return;
    try {
      const { pending } = await submit();
      if (pending) {
        showToast("Join request sent — that class is private, so a room parent there has to approve you.");
      }
      onFinish();
    } catch (err) {
      /* the profile half may already be in — the store remembers, so pressing
         Finish Setup again only retries what failed */
      showToast(
        useSignupStore.getState().profileSaved
          ? `Your profile is saved, but the class step didn't finish: ${err.message}`
          : err.message
      );
    }
  };

  return (
    <>
      <div className="ob-eyebrow">Your Class</div>
      <h1>Create your <em>class group</em> 🏫</h1>
      <p className="sub">This is the parent group for the class you're a room parent for.</p>

      <div className="form-stack">
        <Field label="Which grade are you a room parent for?">
          <div className="ob-grade-list boxed">
            {grades.map((g) => {
              const on = roomParentGrade === g.networkId;
              return (
                <button
                  type="button"
                  className={`ob-grade ${on ? 'on' : ''}`}
                  key={g.networkId}
                  onClick={() => pickGrade(g.networkId)}
                >
                  <span className="mk">{gradeMark(g.name)}</span>
                  <span className="nm">{g.name} · {g.school}</span>
                  <span className="ob-check">{on && <IconCheck />}</span>
                </button>
              );
            })}
          </div>
        </Field>

        {/* Someone else may already run this class — joining theirs beats
            creating a second group for the same room of children. */}
        {roomParentGrade && classesLoading && !existing.length && (
          <div className="ob-loading"><Spinner variant="green" /> Checking for existing classes…</div>
        )}
        {!!existing.length && (
          <Field label="Already set up? Join it instead">
            <div className="ob-class-list">
              {existing.map((c) => {
                const on = joinClassId === c.networkId;
                return (
                  <button
                    type="button"
                    className={`ob-class ${on ? 'on' : ''}`}
                    key={c.networkId}
                    onClick={() => set({ joinClassId: on ? '' : c.networkId })}
                  >
                    <span className="mk">
                      {c.image ? <img src={c.image} alt="" /> : '🏫'}
                    </span>
                    <span className="nm">
                      <b>{c.title}</b>
                      {c.description ? ` · ${c.description}` : ''}
                      <span className="ct"> {roomParentCount(c.roomParents)}</span>
                    </span>
                    <span className="ob-check">{on && <IconCheck />}</span>
                  </button>
                );
              })}
            </div>
          </Field>
        )}

        <Field
          label="Class Name"
          hint={
            joining ? (
              "You're joining an existing class — no need to name a new one."
            ) : nameTooShort ? (
              <span style={{ color: 'var(--red)' }}>Use at least {MIN_CLASS_NAME} characters.</span>
            ) : (
              'Most room parents name it after the teacher.'
            )
          }
        >
          <input
            type="text"
            value={joining ? '' : className}
            placeholder="e.g. Mrs. Taylor's Class"
            maxLength={100}
            disabled={joining}
            onChange={(e) => set({ className: e.target.value })}
          />
        </Field>

        <div className="ob-note">
          <span className="ic">i</span>
          {joining
            ? "Joining adds you to that class's existing hub — its group chat and consent form links are already set up."
            : 'Naming your class creates its hub — the class group chat and consent form links are generated for it automatically.'}
        </div>

        <button className="btn btn-primary btn-primary-full" disabled={!complete || submitting} onClick={finish}>
          {submitting ? <><Spinner /> Setting up…</> : 'Finish Setup'}
        </button>
      </div>
    </>
  );
}
